/* External */
import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';

/* Components */
import LanderLayout from '../../../components/LanderLayout';
import { OpenAgreementCta, SignOffCta } from './buttons';
import SettingSection from './SettingSection';
import SignOffConfirmation from './SignOffConfirmation';
import SystemInfo from './SystemInfo';

/* Constants */
import { PRIVACY_POLICY_PAGE, TERMS_AND_CONDITIONS_PAGE } from '../../../components/TermsOfService/constants';

/* Hooks */
import useOnLeftArrowPress from '../../../hooks/useOnLeftArrowPress';
import { useOpenSidebar } from '../../../providers/SidebarStateContext';

/* Providers */
import { useUserAccount } from '../../../providers/UserProvider';
import { useGtmLocalContext } from '../../../providers/GtmLocalProvider';
import { useCountry } from '../../../providers/ConfigProvider';

/* Utils */
import { Direction } from '../../../utils/spatialNavigation';
import { noOp } from '../../../utils/functions';
import { SETTINGS } from '../../../utils/focusKey';
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../utils/googleTags';

/* Styles */
import './Setting.scss';

function Setting({ setFocus }) {
  const location = useLocation();
  const goToSidebar = useOpenSidebar();
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [lastAgreementFocus, setLastAgreementFocus] = useState(SETTINGS.OPEN_TERMS_AND_CONDITIONS);

  const { username } = useUserAccount() || {};

  const handleGoToSidebar = useOnLeftArrowPress(goToSidebar);
  const country = useCountry();

  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);

  const settingsCommonTagEvent = useCallback(({
    subCategory,
    eventName,
  }) => {
    commonTaggingEvent({
      category: GTM_CATEGORIES.SETTINGS_DATA,
      subCategory,
      eventName,
    });
  }, [commonTaggingEvent]);

  useEffect(() => {
    if (tagsMountRef.current && !openConfirmation && country) {
      settingsCommonTagEvent({
        subCategory: GTM_SUBCATEGORIES.SETTINGS.SETTINGS,
        eventName: GTM_EVENT_NAMES.PAGE_VIEW,
      });
      tagsMountRef.current = false;
    }
  }, [settingsCommonTagEvent, country, openConfirmation]);

  const handleSignOffArrowPress = (direction) => ({
    [Direction.LEFT]: () => handleGoToSidebar(direction),
    [Direction.RIGHT]: () => setFocus(lastAgreementFocus),
  }[direction]);
  const handleSignOffEnterPress = () => {
    setOpenConfirmation(true);
    settingsCommonTagEvent({
      subCategory: GTM_SUBCATEGORIES.SETTINGS.SETTINGS,
      eventName: GTM_EVENT_NAMES.SETTINGS.LOG_OUT,
    });
  };

  const handleAgreementCtaLeftArrowPress = (direction) => (focusKey) => ({
    [Direction.LEFT]: setLastAgreementFocus(focusKey),
  }[direction]);

  const handleSignOffCancelPress = useCallback(() => {
    setOpenConfirmation(false);
    setFocus(SETTINGS.EXIT);
    settingsCommonTagEvent({
      subCategory: GTM_SUBCATEGORIES.SETTINGS.LOG_OUT_CONFIRMATION,
      eventName: GTM_EVENT_NAMES.SETTINGS.CANCEL,
    });
  }, [setFocus, setOpenConfirmation, settingsCommonTagEvent]);

  useEffect(() => {
    const { reviewedAgreement } = location.state || {};
    const focusKey = {
      [PRIVACY_POLICY_PAGE]: SETTINGS.OPEN_PRIVACY_POLICY,
      [TERMS_AND_CONDITIONS_PAGE]: SETTINGS.OPEN_TERMS_AND_CONDITIONS,
      default: SETTINGS.EXIT,
    }[reviewedAgreement || 'default'];

    setFocus(focusKey);
  }, [setFocus, location]);

  return openConfirmation
    ? (
      <SignOffConfirmation
        onCancelPress={handleSignOffCancelPress}
        settingsCommonTagEvent={settingsCommonTagEvent}
      />
    ) : (
      <>
        <LanderLayout className="dtv-html5-c-setting">
          <div className="dtv-html5-c-setting__column dtv-html5-c-setting__left">
            <SettingSection sectionTitleId="subscription">
              <p className="dtv-html5-c-setting__email">
                {username}
              </p>
            </SettingSection>

            <SettingSection>
              <SignOffCta
                onArrowPress={handleSignOffArrowPress}
                onEnterPress={handleSignOffEnterPress}
              />
            </SettingSection>
          </div>

          <div className="dtv-html5-c-setting__column dtv-html5-c-setting__right">
            <SettingSection sectionTitleId="help">
              <p className="dtv-html5-c-setting__help">
                <FormattedMessage id="settings.help.text" />
              </p>
            </SettingSection>

            <SettingSection sectionTitleId="legal">
              <OpenAgreementCta
                type="termsAndConditions"
                onArrowPress={handleAgreementCtaLeftArrowPress}
                settingsCommonTagEvent={settingsCommonTagEvent}
              />
              <OpenAgreementCta
                type="privacyPolicy"
                onArrowPress={handleAgreementCtaLeftArrowPress}
                settingsCommonTagEvent={settingsCommonTagEvent}
              />
            </SettingSection>
          </div>
        </LanderLayout>

        <SystemInfo />
      </>
    );
}

Setting.defaultProps = {
  setFocus: noOp,
};

Setting.propTypes = {
  setFocus: PropTypes.func,
};

export default memo(withFocusable()(memo(Setting)));
